import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { TimePeriod } from 'app/components/pages/Extranet/ExtranetInventory/Legacy/type';
import type {
  ExperienceBulkEditPayload,
  OpeningDays,
} from 'app/typings/core/extranet/experiences/experienceBulkEdit';
import { formatDateWithoutHour } from 'app/utils/dates';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS as INVENTORY_CORE_QUERY_KEYS } from '../inventory';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export function useValidateExperienceBulkEdit(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ExperienceBulkEditPayload) => {
      return apiMutation(
        'POST',
        {
          path: ENDPOINTS.experienceBulkValidate,
          params: {
            id: hotelId.toString(),
            experienceId: payload.experienceId.toString(),
          },
        },
        payload
      ) as Promise<OpeningDays>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.experienceBulkSave, hotelId],
      });
    },
    onError: () => {
      message.error('Error while validating activity bulk edit');
    },
  });
}

export function useSaveExperienceBulkEdit(
  hotelId: number,
  currentRange?: TimePeriod
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ExperienceBulkEditPayload) => {
      return apiMutation(
        'POST',
        {
          path: ENDPOINTS.experienceBulkSave,
          params: {
            id: hotelId.toString(),
            experienceId: payload.experienceId.toString(),
          },
        },
        payload
      ) as Promise<void>;
    },
    onSuccess: () => {
      message.success('Experience bulk edit openings saved successfully');
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.experienceBulkSave, hotelId],
      });

      queryClient.invalidateQueries({
        queryKey: [
          INVENTORY_CORE_QUERY_KEYS.inventory,
          {
            hotelId,
            startDate: formatDateWithoutHour(
              currentRange?.startDate ?? new Date()
            ),
            endDate: formatDateWithoutHour(currentRange?.endDate ?? new Date()),
          },
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [INVENTORY_CORE_QUERY_KEYS.packScore, { hotelId }],
      });
    },
    onError: () => {
      message.error('Error while saving experience bulk edit openings');
    },
  });
}
